import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  IconButton,
  Box,
  DialogActions,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Close as CloseIcon,
  PersonAdd,
  Phone,
  AccountCircle,
  LocationOn,
  Email,
  AssignmentInd,
} from "@mui/icons-material";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import "../../styles/NewCustomerForm.css";
import {
  selectCityRequest,
  selectCountryRequest,
  selectStateRequest,
} from "../../redux/Common/selectorAction";
import {
  fetchServiceTypeRequest,
  fetchTransitServiceTypeRequest,
  resetBookingDetails,
} from "../../redux/Bookings/BookingAction";
import { fetchAllAirportRequest } from "../../redux/Airports/actions/airportActions";
import CustomButton from "../../component/ui/FormFields/Button";
import { useNavigate } from "react-router-dom";
import {
  addCustomerRequest,
  resetCustomerState,
} from "../../redux/Customers/actions/customerActions";
import { Home } from "lucide-react";
import { toast } from "react-toastify";
import { INSTANT_BOOKING_AIRPORT_DETAILS } from "../../component/common/Routes/AppRoutes";
import { convertDateFormat } from "../../Style";

const NewCustomerForm = ({ open, onClose, customerDetails }) => {
  const successMessage = useSelector(
    (state) => state.customers.success?.add || false
  );

  const customerDetail = useSelector(
    (state) => state.customers?.customerDetails?.data?.MemberInfo || false
  );

  const loading = useSelector(
    (state) => state.customers.customerDetails?.loading
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const errorMessage = useSelector(
    (state) => state.customers.customerDetails.error
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      customer_id: customerDetails?.customer_serial_no || "",
      first_name: "",
      last_name: "",
      email: "",
      airport: "",
      country_code: "",
      phone: "",
      arrival_departure_type: "",
      arrival_date: "",
      arrival_time: "",
      departure_date: "",
      departure_time: "",
    },
  });
  const passportNumber = watch("passport_number");
  const aadharNumber = watch("aadhar_number");
  useEffect(() => {
    if (successMessage) {
      toast.success("Customer Inserted Successfully!", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          navigate(INSTANT_BOOKING_AIRPORT_DETAILS, {
            state: {
              customerDetails: customerDetail,
            },
          });
          dispatch(resetCustomerState());
        },
      });
    }
    if (errorMessage) {
      if (errorMessage.message === "Email must be unique") {
        setError("email", {
          type: "server",
          message: "Email must be unique",
        });
      }
      if (errorMessage.message === "Contact must be unique") {
        setError("phone", {
          type: "server",
          message: "Contact must be unique",
        });
      }
    }
  }, [successMessage, errorMessage, navigate, dispatch, setError]);
  useEffect(() => {
    if (customerDetails) {
      reset((prevValues) => ({
        ...prevValues,
        customer_id:
          customerDetails?.customer_serial_no +
          " - " +
          customerDetails.display_name,
      }));
    }
  }, [customerDetails, reset]);

  const formValue = watch();

  const countries = useSelector((state) => state.selector?.country?.data);
  const countryForCode = countries?.map((value) => ({
    label: `+${value.phonecode} - ${value.short_name}`,
    value: value.phonecode,
  }));

  useEffect(() => {
    dispatch(resetCustomerState());
    dispatch(selectCountryRequest());
  }, []);

  const airport_type = formValue.service_type?.value === "Domestic" ? 1 : 2;
  const airport_id = formValue.airport?.value;
  const transit_type = formValue.transit_service?.value;
  const service_type =
    formValue.arrival_departure_type?.value === "Arrival"
      ? 1
      : formValue.arrival_departure_type?.value === "Departure"
      ? 2
      : 3;
  const onSubmit = (data) => {
    // if (formValue?.aadhar_number === "" && formValue?.passport_number === "") {
    //   setError("aadhar_number", {
    //     type: "server",
    //     message: "Aadhar or Passport Number is needed",
    //   });
    //   setError("passport_number", {
    //     type: "server",
    //     message: "Aadhar or Passport Number is needed",
    //   });
    //   return;
    // }
    const customerData = {
      first_name: data.first_name,
      last_name: data.last_name,
      display_name: `${data.first_name} ${data.last_name}`,
      email: data.email,
      phone: data.phone,

      dob: convertDateFormat(data.dob),
      passport_number: data?.passport_number,
      aadhar_number: data?.aadhar_number,
      nationality: data?.nationality,
      country_code: data?.country_code.value,
      address1: data.address1,
      address2: data.address2,
      city: data.city?.value || data.city,
      state: data.state?.value || data.state,
      country: data.country?.value || data.country,
      pincode: data.pincode,
      profile_url: null,
      user_type: "3",
      gender_type: data?.gender_type,
    };
    dispatch(addCustomerRequest(customerData));

    // dispatch(insertInstantBookingRequest(customerData));
  };

  useEffect(() => {
    dispatch(fetchAllAirportRequest());
  }, []);

  useEffect(() => {
    if (
      formValue.service_type &&
      formValue.arrival_departure_type?.value &&
      formValue.airport
    ) {
      dispatch(
        fetchServiceTypeRequest({ service_type, airport_type, airport_id })
      );
    }
  }, [
    formValue.service_type,
    formValue.arrival_departure_type,
    formValue.airport,
  ]);

  useEffect(() => {
    if (
      formValue.service_type &&
      formValue.arrival_departure_type?.value &&
      formValue.airport &&
      formValue.transit_service?.value
    ) {
      dispatch(
        fetchTransitServiceTypeRequest({
          transit_type,
          service_type,
          airport_id,
        })
      );
    }
  }, [formValue.transit_service]);

  const states = useSelector((state) => state.selector?.state?.data);
  const cities = useSelector((state) => state.selector?.city?.data);
  const countryId = formValue.country?.value;
  const stateId = formValue.state?.value;
  const countryName = countries?.map((country) => {
    return { value: country?.id, label: country?.country_name };
  });

  const stateName = states?.map((state) => {
    return { value: state?.id, label: state?.state_name };
  });
  const cityName = cities?.map((city) => {
    return { value: city?.id, label: city?.city_name };
  });

  // useEffect runs whenever `country` changes
  useEffect(() => {
    if (countryId > 0) {
      setValue("state", "");
      setValue("city", "");
      dispatch(selectStateRequest(countryId));
    }
  }, [countryId]);
  // change state
  useEffect(() => {
    if (stateId) {
      setValue("city", "");
      dispatch(selectCityRequest(stateId));
    }
  }, [stateId]);
  const validateIdentityDocument = () => {
    return (
      (passportNumber && passportNumber.trim() !== "") ||
      (aadharNumber && aadharNumber.trim() !== "") ||
      "Either Passport Number or Aadhar Number is required"
    );
  };
  const formFields = [
    {
      name: "first_name",
      label: "First Name *",
      placeholder: "Enter First Name",
      icon: <AccountCircle />,
      rules: { required: "First Name is required" },
      gridSize: 6,
    },
    {
      name: "last_name",
      label: "Last Name *",
      placeholder: "Enter Last Name",
      icon: <AccountCircle />,
      rules: { required: "Last Name is required" },
      gridSize: 6,
    },
    {
      name: "dob",
      label: "Date of Birth *",
      placeholder: "Select date of birth",
      type: "dob",
      rules: { required: "Date of Birth is required" },

      gridSize: 6,
    },
    {
      name: "email",
      label: "Email *",
      placeholder: "Enter Email Address",
      icon: <Email />,
      rules: {
        required: "Email Address is required",
        pattern: {
          value: /^\S+@\S+\.\S+$/,
          message: "Enter a valid email address",
        },
      },
      gridSize: 6,
    },

    {
      name: "country_code",
      options: countryForCode,
      label: "code *",
      placeholder: "Select Country Code",
      rules: {
        required: "Code is Required",
      },
      gridSize: 2,
    },
    {
      name: "phone",
      label: "Mobile No *",
      placeholder: "Enter Mobile Number",
      icon: <Phone />,
      rules: {
        required: "Mobile Number is required",
        pattern: {
          value: /^\d+$/,
          message: "Enter only numeric digits",
        },
      },
      gridSize: 4,
    },
    {
      name: "nationality",
      label: "Nationality *",
      placeholder: "Enter Nationality",
      type: "text",
      rules: {
        required: "Nationality is required",
        pattern: {
          value: /^[A-Za-z\s]+$/,
          message: "Only alphabets are allowed",
        },
      },
      gridSize: 6,
      inputProps: {
        onInput: (e) => {
          e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
        },
      },
    },
    {
      name: "passport_number",
      label: "Passport Number",
      placeholder: "Enter Passport Number",
      rules: {
        validate: {
          // identityRequired: validateIdentityDocument,
          passportFormat: (value) => {
            if (!value || value.trim() === "") return true;

            const patterns = {
              US: /^[0-9]{9}$/,
              UK: /^[0-9]{9}$|^[A-Z]{2}[0-9]{7}$/,
              EU: /^[A-Z]{1,2}[0-9]{5,7}$/,
              CA: /^[A-Z]{2}[0-9]{6}$/,
              AU: /^[A-Z][0-9]{8}$/,
              IN: /^[A-Z][0-9]{7}$/,
              general: /^[A-Z0-9]{5,12}$/i,
            };

            return (
              patterns.US.test(value) ||
              patterns.UK.test(value) ||
              patterns.EU.test(value) ||
              patterns.CA.test(value) ||
              patterns.AU.test(value) ||
              patterns.IN.test(value) ||
              patterns.general.test(value) ||
              "Please enter a valid passport number"
            );
          },
        },
      },
      gridSize: 6,
    },
    {
      name: "aadhar_number",
      label: "Aadhar Number",
      placeholder: "Enter Aadhar Number",
      type: "Number",
      rules: {
        validate: {
          // identityRequired: validateIdentityDocument,
          aadharFormat: (value) => {
            if (!value || value.trim() === "") return true;

            return (
              /^\d{12}$/.test(value) ||
              "Aadhar Number must be exactly 12 digits"
            );
          },
        },
      },
      gridSize: 6,
    },
    {
      name: "gender_type",
      label: "Gender",
      placeholder: "Select Gender",
      icon: <AssignmentInd />,
      type: "radio",
      radioOptions: [
        { value: "M", label: "Male" },
        { value: "F", label: "Female" },
        { value: "O", label: "Other" },
      ],
      rules: { required: "Gender is required" },
      gridSize: 12,
    },
  ];

  useEffect(() => {
    setValue("service_type", "");
    setValue("arrival_plan", " ");
    setValue("departure_plan", " ");
    setValue("transit_plan", "");
    setValue("transit_service", "");
  }, [formValue.arrival_departure_type]);
  useEffect(() => {
    setValue("arrival_plan", " ");
    setValue("departure_plan", " ");
    setValue("transit_plan", "");
    setValue("transit_service", "");
  }, [formValue.service_type]);

  // Handle cancel button click - only reset form, don't reset booking details
  const handleCancel = () => {
    // Reset the form to its default values
    reset({
      customer_id: customerDetails?.customer_serial_no || "",
      first_name: "",
      last_name: "",
      email: "",
      airport: "",
      country_code: "",
      phone: "",
      arrival_departure_type: "",
      arrival_date: "",
      arrival_time: "",
      departure_date: "",
      departure_time: "",
    });
    
    // Reset customer state (but not booking details)
    dispatch(resetCustomerState());
    
    // Close the dialog
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: "400px",
        },
      }}
    >
      <DialogTitle className="dialog-title">
        <Box className="dialog-box">
          <PersonAdd className="dialog-title-icon" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            New Customer
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            {formFields.map((field) => (
              <Grid item xs={field.gridSize} key={field.name}>
                <ControlledTextInputField
                  name={field.name}
                  control={control}
                  label={field.label}
                  options={field.options}
                  placeholder={field.placeholder}
                  type={field.type || "text"}
                  rules={field.rules}
                  radioOptions={field.radioOptions}
                  fullWidth
                  error={errors[field.name]}
                  helperText={errors[field.name]?.message}
                  size="large"
                  startAdornment={field.icon}
                  instantBooking={field.instantBooking}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <CustomButton
            variant="contained"
            className="cancel-button"
            text=" Cancel"
            type="button"
            onClick={handleCancel}
          />
          <CustomButton
            variant="contained"
            className="orange-button"
            text="Create Customer"
            loading={loading}
            type="submit"
          />
        </DialogActions>
      </form>
    </Dialog>
  );
};
export default NewCustomerForm;